import * as Sentry from "@sentry/react";

/**
 * Wraps fetch() with an AbortController timeout.
 * Default timeout: 300 seconds (5 minutes).
 */
export async function fetchWithTimeout(
  url: string,
  options: RequestInit,
  timeoutMs = 300_000,
): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

export type ScoringErrorKind =
  | 'timeout'
  | 'network'
  | 'rate_limit'
  | 'usage_limit'
  | 'server_error'
  | 'unknown';

export interface ClassifiedError {
  kind: ScoringErrorKind;
  message: string;
  retryable: boolean;
}

/**
 * Classify a caught error or HTTP status into a user-friendly message.
 * Also reports the error to Sentry with classification context.
 */
export function classifyError(err: unknown, status?: number, url?: string): ClassifiedError {
  let classified: ClassifiedError;

  // HTTP status-based classification
  if (status === 429) {
    classified = { kind: 'rate_limit', message: 'Too many requests. Please wait a moment and retry.', retryable: true };
  } else if (status === 402) {
    classified = { kind: 'usage_limit', message: 'Usage limit reached.', retryable: false };
  } else if (status && status >= 500) {
    classified = { kind: 'server_error', message: 'Server error. Please try again.', retryable: true };
  }
  // Exception-based classification
  else if (err instanceof DOMException && err.name === 'AbortError') {
    classified = {
      kind: 'timeout',
      message: 'This is taking longer than expected. You can retry or try again later.',
      retryable: true,
    };
  } else if (err instanceof TypeError) {
    classified = {
      kind: 'network',
      message: 'Connection lost. Please check your internet and try again.',
      retryable: true,
    };
  } else {
    const msg = err instanceof Error ? err.message : 'Something went wrong. Please try again.';
    classified = { kind: 'unknown', message: msg, retryable: true };
  }

  // Report to Sentry with classification context
  Sentry.captureException(err instanceof Error ? err : new Error(classified.message), {
    tags: { errorKind: classified.kind },
    extra: { retryable: classified.retryable, status, url },
  });

  return classified;
}
